import Skeleton from "../../common/Skeleton";

type PerformanceTableSkeletonVariant = "full" | "historical" | "research";

const PerformanceTableSkeleton = ({
  isDashboardChart,
  variant = "full",
  tableHeightClass,
}: {
  isDashboardChart?: boolean;
  variant?: PerformanceTableSkeletonVariant;
  tableHeightClass?: string;
}) => {
  const bodyHeight = tableHeightClass ?? "h-[250px] lg:h-[300px]";

  const renderTableCard = (
    titleWidth: string,
    columnWidths: string[],
    rowCount = 5,
  ) => (
    <div className="bg-white dark:bg-transparent rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="px-2 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <Skeleton className={`${titleWidth} h-5 rounded`} />
        <Skeleton className="w-4 h-4 rounded" />
      </div>
      <div className={`p-2 overflow-hidden ${bodyHeight}`}>
        <div className="flex justify-between gap-2 mb-4 mt-2">
          {columnWidths.map((width, i) => (
            <Skeleton key={i} className={`${width} h-4 rounded`} />
          ))}
        </div>
        <div className="space-y-3">
          {[...Array(rowCount)].map((_, i) => (
            <Skeleton key={i} className="w-full h-7 rounded" />
          ))}
        </div>
      </div>
    </div>
  );

  if (variant === "historical") {
    return renderTableCard("w-64", ["w-16", "w-16", "w-16", "w-16", "w-16"]);
  }

  if (variant === "research") {
    return renderTableCard("w-48", ["w-20", "w-20", "w-20", "w-24"]);
  }

  return (
    <div
      className={
        isDashboardChart
          ? "w-full"
          : "grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-1"
      }
    >
      {/* Historical Performance Skeleton */}
      {isDashboardChart ? (
        <div className="flex h-full min-h-0 w-full flex-col">
          <div className="mb-2 flex shrink-0 justify-between gap-1 px-0.5">
            <Skeleton className="h-3 w-[18%] rounded" />
            <Skeleton className="h-3 w-[14%] rounded" />
            <Skeleton className="h-3 w-[14%] rounded" />
            <Skeleton className="h-3 w-[18%] rounded" />
          </div>
          <div className="min-h-0 flex-1 space-y-2 overflow-hidden">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-7 w-full rounded" />
            ))}
          </div>
        </div>
      ) : (
        <>
          {renderTableCard("w-64", ["w-16", "w-16", "w-16", "w-16", "w-16"])}

          {/* Research Firm Insights Skeleton */}
          {renderTableCard("w-48", ["w-20", "w-20", "w-20", "w-24"])}

          {/* Gauge Chart Skeleton */}
          <div className="bg-white dark:bg-[#121419] rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm xl:col-span-2 2xl:col-span-1">
            <div className="px-2 py-4 border-b border-gray-200 dark:border-gray-700">
              <Skeleton className="w-32 h-5 rounded" />
            </div>
            <div className="relative flex h-[250px] lg:h-[300px] items-center justify-center p-4">
              <Skeleton className="w-[200px] h-[100px] rounded-t-full" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PerformanceTableSkeleton;
